/**
 * A large chunk of this file is client side sorting and filtering.
 * Delete them when we move to server-side filtering and sorting.
 */

import type { Filter, FilterMatchMode } from "@/shared/components/ResourceView";

import { isSchedule, ManagedByKind, type TriggerRecord } from "./TriggerRecord";

/** What fires a trigger, as the list's "Fires on" filter compares it. */
export type TriggerFiresOn = "events" | "schedule";

/**
 * Who owns a trigger's definition, as the list's "Managed by" filter compares
 * it: Roboto, an ingestion rule, or (`"user"`) the person who wrote it.
 */
export type TriggerOwner = ManagedByKind | "user";

/** A sort on the list's Name or Modified column. */
export interface TriggerSort {
  columnId: string;
  direction: "asc" | "desc";
}

/** Which triggers the list shows: the filter builder's rows. */
export interface TriggerFilterParams {
  filters: readonly Filter[];
  matchMode: FilterMatchMode;
}

/** What the table asks the list for: the sort, the filters, and the page. */
export interface TriggerViewParams {
  sort: TriggerSort | null;
  filter: TriggerFilterParams;
  page: number;
  pageSize: number;
}

function isNegative(comparator: string): boolean {
  return comparator.startsWith("NOT_");
}

function stringMatches(
  haystack: string,
  comparator: string,
  needle: string,
): boolean {
  const h = haystack.toLowerCase();
  const n = needle.toLowerCase();
  switch (comparator) {
    case "EQUALS":
      return h === n;
    case "NOT_EQUALS":
      return h !== n;
    case "CONTAINS":
    case "LIKE":
      return h.includes(n);
    case "NOT_CONTAINS":
    case "NOT_LIKE":
      return !h.includes(n);
    default:
      return true;
  }
}

function matchesStringFilter(
  haystack: string,
  comparator: string,
  values: readonly string[],
): boolean {
  const needles = values.filter((value) => value.trim().length > 0);
  if (needles.length === 0) {
    return true;
  }
  return isNegative(comparator)
    ? needles.every((needle) => stringMatches(haystack, comparator, needle))
    : needles.some((needle) => stringMatches(haystack, comparator, needle));
}

function matchesMembership(
  owned: readonly string[],
  comparator: string,
  values: readonly string[],
): boolean {
  if (values.length === 0) {
    return true;
  }
  const anyOwned = values.some((value) => owned.includes(value));
  return isNegative(comparator) ? !anyOwned : anyOwned;
}

function firesOnOf(trigger: TriggerRecord): TriggerFiresOn {
  return isSchedule(trigger.fires_on) ? "schedule" : "events";
}

function ownerOf(trigger: TriggerRecord): TriggerOwner {
  return trigger.managed_by?.kind ?? "user";
}

const FILTER_MATCHERS = {
  name: (trigger, filter) =>
    filter.type === "string" &&
    matchesStringFilter(trigger.name, filter.comparator, filter.values),
  modified_by: (trigger, filter) =>
    filter.type === "string" &&
    matchesStringFilter(trigger.modified_by, filter.comparator, filter.values),
  events: (trigger, filter) =>
    filter.type === "set" &&
    matchesMembership(
      isSchedule(trigger.fires_on) ? [] : trigger.fires_on.events,
      filter.comparator,
      filter.values.map((option) => option.value),
    ),
  targets: (trigger, filter) =>
    filter.type === "set" &&
    matchesMembership(
      trigger.targets.map((target) => target.type),
      filter.comparator,
      filter.values.map((option) => option.value),
    ),
  fires_on: (trigger, filter) =>
    filter.type === "enum" &&
    matchesMembership(
      [firesOnOf(trigger)],
      filter.comparator,
      filter.values.map((option) => option.value),
    ),
  managed_by: (trigger, filter) =>
    filter.type === "enum" &&
    matchesMembership(
      [ownerOf(trigger)],
      filter.comparator,
      filter.values.map((option) => option.value),
    ),
  enabled: (trigger, filter) =>
    filter.type === "boolean" &&
    (filter.values.length === 0 || trigger.enabled === filter.values[0]),
} satisfies Record<string, (trigger: TriggerRecord, filter: Filter) => boolean>;

/** A field the triggers list can be filtered on. */
export type TriggerFilterField = keyof typeof FILTER_MATCHERS;

/** Whether `trigger` passes `filter`. A filter on any other field matches nothing. */
function matchesFilter(trigger: TriggerRecord, filter: Filter): boolean {
  return (
    Object.hasOwn(FILTER_MATCHERS, filter.field) &&
    FILTER_MATCHERS[filter.field as TriggerFilterField](trigger, filter)
  );
}

/**
 * Whether the triggers list shows `trigger` at all. A rule's trigger is hidden
 * while the rule's auto-ingest is off: the rule is where it is turned back on.
 */
export function isListedTrigger(trigger: TriggerRecord): boolean {
  return trigger.enabled || ownerOf(trigger) !== ManagedByKind.IngestionRule;
}

function passesFilters(
  trigger: TriggerRecord,
  { filters, matchMode }: TriggerFilterParams,
): boolean {
  if (!isListedTrigger(trigger)) {
    return false;
  }
  if (filters.length === 0) {
    return true;
  }
  return matchMode === "OR"
    ? filters.some((filter) => matchesFilter(trigger, filter))
    : filters.every((filter) => matchesFilter(trigger, filter));
}

/**
 * The listed triggers that pass the filter builder's rows, joined by
 * `matchMode` (AND requires every row, OR at least one; no rows pass
 * everything). The in-memory equivalent of the server-side `filterToCondition`
 * path.
 */
export function filterTriggers(
  triggers: readonly TriggerRecord[],
  filter: TriggerFilterParams,
): TriggerRecord[] {
  return triggers.filter((trigger) => passesFilters(trigger, filter));
}

/** Whether any filter row narrows the list. */
export function hasActiveFilters(filter: TriggerFilterParams): boolean {
  return filter.filters.length > 0;
}

/**
 * Orders two triggers by `sort`: Name ignores case, anything else is Modified,
 * by instant. With no sort, every pair is equal.
 */
function compareBy(
  sort: TriggerSort | null,
): (a: TriggerRecord, b: TriggerRecord) => number {
  if (sort === null) {
    return () => 0;
  }
  const factor = sort.direction === "asc" ? 1 : -1;
  return sort.columnId === "name"
    ? (a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) *
        factor
    : (a, b) => (Date.parse(a.modified) - Date.parse(b.modified)) * factor;
}

/** `triggers` sorted by `sort`, or in the order given when there's no sort. */
export function sortTriggers(
  triggers: readonly TriggerRecord[],
  sort: TriggerSort | null,
): TriggerRecord[] {
  return [...triggers].sort(compareBy(sort));
}

/**
 * `triggers` in the order of `previousRows`, by `trigger_id`. Triggers not in
 * `previousRows` go where `sort` puts them; rows with no trigger drop out.
 */
export function preserveOrder(
  previousRows: readonly TriggerRecord[],
  triggers: readonly TriggerRecord[],
  sort: TriggerSort | null,
): TriggerRecord[] {
  const compare = compareBy(sort);
  const byId = new Map(
    triggers.map((trigger) => [trigger.trigger_id, trigger]),
  );
  const ordered = previousRows.flatMap((row) => byId.get(row.trigger_id) ?? []);
  const previousIds = new Set(previousRows.map((row) => row.trigger_id));
  const added = triggers.filter(
    (trigger) => !previousIds.has(trigger.trigger_id),
  );
  for (const trigger of sortTriggers(added, sort)) {
    const before = ordered.findIndex((other) => compare(trigger, other) < 0);
    ordered.splice(before === -1 ? ordered.length : before, 0, trigger);
  }
  return ordered;
}

/**
 * Whether the list shows rows in the same order under `a` and `b`: the same
 * sort and filters, however the params were built. The page doesn't count.
 */
export function isInSameOrder(
  a: TriggerViewParams,
  b: TriggerViewParams,
): boolean {
  return orderKey(a) === orderKey(b);
}

function orderKey({ sort, filter }: TriggerViewParams): string {
  return JSON.stringify([sort?.columnId, sort?.direction, filter]);
}

/** One page of the list's rows, and where it sits among them. */
export interface PaginationState {
  rows: TriggerRecord[];
  /** Rows across every page. */
  total: number;
  hasNextPage: boolean;
}

/** Page `page` (from 0) of `rows`, `pageSize` to a page. */
export function paginate(
  rows: readonly TriggerRecord[],
  page: number,
  pageSize: number,
): PaginationState {
  const start = page * pageSize;
  const end = start + pageSize;
  return {
    rows: rows.slice(start, end),
    total: rows.length,
    hasNextPage: end < rows.length,
  };
}

/** A copy of `list` with the trigger named `name` replaced by `record`. */
export function replaceTrigger(
  list: readonly TriggerRecord[],
  name: string,
  record: TriggerRecord,
): TriggerRecord[] {
  return list.map((trigger) => (trigger.name === name ? record : trigger));
}

/** A copy of `list` without the trigger named `name`. */
export function removeTrigger(
  list: readonly TriggerRecord[],
  name: string,
): TriggerRecord[] {
  return list.filter((trigger) => trigger.name !== name);
}
